import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { PlusIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useCreateUserInvitation } from "@/hooks/mutations";
import { useSetNewCompanyUserInCompanyUsersForSettings } from "@/hooks/sets";
import { Company, UserInvitationStatus } from "@/types";

const inviteUserSchema = z.object({
  name: z
    .string()
    .min(1, "Required")
    .refine(
      (arg) => {
        const words = arg.trim().split(" ");
        return words.length >= 2;
      },
      {
        message: "First and last name required",
      },
    ),
  email: z.string().min(1, "Required"),
});

export function SettingsInviteUserDialog({
  company,
  currentUserId,
}: {
  company: Company;
  currentUserId: number;
}) {
  const { mutate: createInvitation, status } = useCreateUserInvitation();
  const [isOpen, setIsOpen] = useState(false);
  const setNewCompanyUserInCompanyUsersForSettings =
    useSetNewCompanyUserInCompanyUsersForSettings();

  const form = useForm<z.infer<typeof inviteUserSchema>>({
    defaultValues: {
      name: "",
      email: "",
    },
    resolver: zodResolver(inviteUserSchema),
  });

  const handleSubmit = (data: z.infer<typeof inviteUserSchema>) => {
    const firstName = data.name.split(" ")[0];
    const lastName = data.name.split(" ").slice(1).join(" ");

    createInvitation(
      {
        createUserInvitationRequest: {
          firstName,
          lastName,
          inviteeEmail: data.email,
        },
      },
      {
        onSuccess: () => {
          setNewCompanyUserInCompanyUsersForSettings({
            companyUserToAdd: {
              email: data.email,
              firstName,
              lastName,
              invitationStatus: UserInvitationStatus.PENDING,
              isInvited: true,
              inviterId: currentUserId,
            },
          });
          form.reset();
          setIsOpen(false);
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full">
          <PlusIcon />
          Add user
        </Button>
      </DialogTrigger>
      <DialogContent>
        <div className="flex flex-col gap-1">
          <p className="text-2xl font-semibold text-slate-700">
            Add a user to {company.name}
          </p>
          <p className="text-sm text-slate-700">
            This will grant full user access to the platform
          </p>
        </div>
        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="First and last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Email" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                className="rounded-full"
                type="button"
                onClick={() => {
                  setIsOpen(false);
                }}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                className="rounded-full"
                disabled={status === "pending"}
                type="submit"
              >
                Add user
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
